import { ApiResponse, User, LoginFormData, RegisterServiceUserFormData, ForgotPasswordFormData, ResetPasswordFormData, ProviderProfileFormData, ProviderProfile, ProviderProfileResponse, ReviewsSummary, UpdateProviderProfileData, GetAllProvidersRequest, GetAllProvidersResponse, GeoSearchRequest, GeoSearchResponse, CreateBookingRequest, CreateBookingResponse, BookingActionResponse, BookingDetailsResponse, UserProfileFormData, UserProfileResponse, UpdateUserProfileData, CreateReviewRequest, CreateReviewResponse, GetProviderReviewsResponse, Review, CreatePostFormData, CreatePostResponse, UpdatePostFormData, UpdatePostResponse, GetUserPostsResponse, DeletePostResponse, GetAllPostsResponse, ContactPostRequest, ContactPostResponse, StartProviderChatRequest, StartProviderChatResponse, SendUserReplyRequest, SendUserReplyResponse, GetUserChatsResponse, GetProviderChatsResponse, BookNowRequest, BookNowResponse, AdminSendOtpRequest, AdminSendOtpResponse, AdminVerifyOtpRequest, AdminVerifyOtpResponse, AdminProviderListRequest, AdminProviderListResponse, AdminProviderDetailsRequest, AdminProviderDetailsResponse, AdminBlockProviderRequest, AdminBlockProviderResponse, AdminUpdateNoteRequest, AdminUpdateNoteResponse, GetNotificationsResponse, MarkNotificationAsReadResponse, MarkAllNotificationsAsReadResponse, DeleteNotificationResponse, AdminReviewListRequest, AdminReviewListResponse, LowRatingReviewsResponse, AdminReviewActionResponse, AdminBookingListRequest, AdminBookingListResponse, AdminBookingDetailsRequest, AdminBookingDetailsResponse, AdminUpdateBookingStatusRequest, AdminUpdateBookingStatusResponse, AdminCancelBookingRequest, AdminCancelBookingResponse, GetDeleteRequestsRequest, GetDeleteRequestsResponse, SearchDeleteRequestsRequest, SearchDeleteRequestsResponse, DeleteRequestDashboardRequest, DeleteRequestDashboardResponse, ApproveDeleteRequestRequest, ApproveDeleteRequestResponse, RejectDeleteRequestRequest, RejectDeleteRequestResponse, BulkApproveDeleteRequestsRequest, BulkApproveDeleteRequestsResponse, BulkRejectDeleteRequestsRequest, BulkRejectDeleteRequestsResponse, ReactivateAccountRequest, ReactivateAccountResponse, UpcomingBookingsResponse, BookingHistoryResponse, GetBookingHistoryRequest, DashboardSummary, DashboardSummaryResponse } from '../types';
import { transformDeleteRequestsResponse } from '../utils/deleteRequestTransform';

const API_BASE_URL = 'https://api.nursingcarebooking.com';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiClient = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      data: data,
      message: data.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const loginUser = async (formData: LoginFormData): Promise<ApiResponse<{ user: User; token: string }>> => {
  const response = await apiClient<{ user: User; token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email: formData.email,
      password: formData.password,
    }),
  });

  if (response.success && response.data) {
    const user = response.data.user;

    if (user.providerId && !user.userId) {
      user.userId = String(user.providerId);
    }

    localStorage.setItem('authToken', response.data.token);
    localStorage.setItem('userData', JSON.stringify(user));
  }

  return response;
};

export const registerServiceUser = async (
  formData: RegisterServiceUserFormData
): Promise<ApiResponse<{ message: string }>> => {
  return apiClient<{ message: string }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({
      email: formData.email,
      password: formData.password,
      name: formData.name,
      phone: formData.phone,
      role: formData.role,
    }),
  });
};

export const resendOtp = async (
  email: string,
  password: string,
  name: string
): Promise<ApiResponse<{ message: string }>> => {
  return apiClient<{ message: string }>('/api/auth/resend-otp', {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      name,
    }),
  });
};

export const verifyOtp = async (
  email: string,
  otp: string
): Promise<ApiResponse<{ message: string; user: { id: string; name: string; email: string; userId: string } }>> => {
  return apiClient<{ message: string; user: { id: string; name: string; email: string; userId: string } }>('/api/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({
      email,
      otp,
    }),
  });
};


export const logoutUser = async (): Promise<ApiResponse<null>> => {
  console.log('Logout API called');

  await new Promise(resolve => setTimeout(resolve, 500));

  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');

  return {
    success: true,
    data: null,
    message: 'Logout successful',
  };
};

export const getCurrentUser = async (): Promise<ApiResponse<User>> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
    return {
      success: false,
      error: 'Not authenticated',
    };
  }

  await new Promise(resolve => setTimeout(resolve, 500));

  const storedUserData = localStorage.getItem('userData');
  const userData = storedUserData ? JSON.parse(storedUserData) : null;

  const mockUser: User = {
    id: userData?.id || '1',
    userId: userData?.userId || (userData?.providerId ? String(userData.providerId) : '1'),
    providerId: userData?.providerId,
    email: userData?.email || 'user@example.com',
    name: userData?.name || 'John Doe',
    role: userData?.role || 'user',
    phone: userData?.phone || '07700 900000',
    createdAt: userData?.createdAt || new Date().toISOString(),
    hasProviderProfile: userData?.hasProviderProfile,
    hasUserProfile: userData?.hasUserProfile,
    isProfileCompleted: userData?.isProfileCompleted,
  };

  return {
    success: true,
    data: mockUser,
  };
};

export const forgotPassword = async (formData: ForgotPasswordFormData): Promise<ApiResponse<{ message: string }>> => {
  return apiClient<{ message: string }>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({
      email: formData.email,
    }),
  });
};

export const resetPassword = async (formData: ResetPasswordFormData): Promise<ApiResponse<{ message: string }>> => {
  return apiClient<{ message: string }>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email: formData.email,
      otp: formData.otp,
      password: formData.newPassword,
    }),
  });
};

export const deleteUser = async (userId: string, userRole: string, reason: string): Promise<ApiResponse<{ message: string; deletedUserId?: number; deletedProviderId?: number }>> => {
  // Both provider and service user use the same POST endpoint: /api/auth/delete
  const body = userRole === 'provider'
    ? {
        providerId: parseInt(userId, 10),
        reason: reason,
      }
    : {
        userId: parseInt(userId, 10),
        reason: reason,
      };

  return apiClient<{ message: string }>('/api/auth/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const createProviderProfile = async (
  formData: ProviderProfileFormData
): Promise<ApiResponse<ProviderProfileResponse>> => {
  const formDataPayload = new FormData();

  formDataPayload.append('providerId', formData.providerId);
  formDataPayload.append('name', formData.name);
  formDataPayload.append('phone', formData.phone);
  formDataPayload.append('address', formData.address);
  formDataPayload.append('postalCode', formData.postalCode);
  formDataPayload.append('city', formData.city);
  formDataPayload.append('typeOfCare', JSON.stringify(formData.typeOfCare));
  formDataPayload.append('location', formData.city);
  formDataPayload.append('serviceRadiusKm', formData.serviceRadiusKm.toString());

  if (formData.latitude !== undefined) {
    formDataPayload.append('latitude', formData.latitude.toString());
  }
  if (formData.longitude !== undefined) {
    formDataPayload.append('longitude', formData.longitude.toString());
  }

  formDataPayload.append('services', JSON.stringify(formData.services));
  formDataPayload.append('credentials', JSON.stringify(formData.credentials));
  formDataPayload.append('pricing', JSON.stringify(formData.pricing));
  formDataPayload.append('availability', JSON.stringify(formData.availability));

  if (formData.avatar) {
    formDataPayload.append('avatar', formData.avatar);
  }

  if (formData.pictures && formData.pictures.length > 0) {
    formData.pictures.forEach((picture) => {
      formDataPayload.append('pictures', picture);
    });
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/provider/create`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      data: data,
      message: data.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getProviderProfile = async (providerId: string): Promise<ProviderProfileResponse> => {
  const response = await apiClient<any>(`/api/provider/profile/providerid`, {
  method: 'POST',
  body: JSON.stringify({ providerId: Number(providerId) }),
});

  if (response.success && response.data) {
    const apiData = response.data;
    const provider = apiData.provider;

    if (provider) {
      if (provider.isBlocked === true) {
        console.log('Provider is blocked, denying access');
        return {
          success: false,
          provider: null as any,
          message: 'This provider is not available',
        };
      }

      let latitude: number | undefined = provider.latitude;
      let longitude: number | undefined = provider.longitude;

      if (provider.locationGeo && provider.locationGeo.coordinates && Array.isArray(provider.locationGeo.coordinates)) {
        longitude = provider.locationGeo.coordinates[0];
        latitude = provider.locationGeo.coordinates[1];
      }

      const typeOfCareArray = Array.isArray(provider.typeOfCare)
        ? provider.typeOfCare
        : (typeof provider.typeOfCare === 'string' && provider.typeOfCare.trim() !== '')
          ? [provider.typeOfCare]
          : [];

      const transformedProvider: ProviderProfile = {
        ...provider,
        providerId: provider.providerId || Number(providerId),
        userId: provider.providerId || provider.userId,
        typeOfCare: typeOfCareArray,
        address: provider.address || '',
        postalCode: provider.postalCode || '',
        city: provider.city || provider.location || '',
        latitude,
        longitude,
        gallery: provider.pictures || provider.gallery || [],
      };

      console.log('Provider Profile Data:', {
        providerId: transformedProvider.providerId,
        avatar: transformedProvider.avatar,
        galleryCount: transformedProvider.gallery?.length || 0,
        galleryUrls: transformedProvider.gallery,
      });

      const rawSummary = apiData.reviewsSummary;
      const reviewsSummary: ReviewsSummary | undefined = rawSummary
        ? {
            averageRating: rawSummary.averageRating ?? 0,
            totalReviews: rawSummary.totalReviews ?? 0,
            ratingDistribution: {
              1: rawSummary.ratingDistribution?.['1'] ?? 0,
              2: rawSummary.ratingDistribution?.['2'] ?? 0,
              3: rawSummary.ratingDistribution?.['3'] ?? 0,
              4: rawSummary.ratingDistribution?.['4'] ?? 0,
              5: rawSummary.ratingDistribution?.['5'] ?? 0,
            },
            recentReviews: rawSummary.recentReviews ?? [],
          }
        : undefined;

      return {
        success: true,
        provider: transformedProvider,
        message: apiData.message,
        reviewsSummary,
      };
    }
  }

  return {
    success: false,
    provider: null as any,
    message: response.error || 'Failed to fetch provider profile',
  };
};

export const updateProviderProfile = async (
  providerId: string,
  profileData: UpdateProviderProfileData
): Promise<ApiResponse<ProviderProfileResponse>> => {
  const formDataPayload = new FormData();

  formDataPayload.append('providerId', providerId);

  if (profileData.name) formDataPayload.append('name', profileData.name);
  if (profileData.phone) formDataPayload.append('phone', profileData.phone);
  if (profileData.address) formDataPayload.append('address', profileData.address);
  if (profileData.postalCode) formDataPayload.append('postalCode', profileData.postalCode);
  if (profileData.city) formDataPayload.append('city', profileData.city);
  if (profileData.typeOfCare) formDataPayload.append('typeOfCare', JSON.stringify(profileData.typeOfCare));
  if (profileData.location) formDataPayload.append('location', profileData.location);
  if (profileData.serviceRadiusKm) formDataPayload.append('serviceRadiusKm', profileData.serviceRadiusKm.toString());

  if (profileData.latitude !== undefined) {
    formDataPayload.append('latitude', profileData.latitude.toString());
  }
  if (profileData.longitude !== undefined) {
    formDataPayload.append('longitude', profileData.longitude.toString());
  }

  if (profileData.services) formDataPayload.append('services', JSON.stringify(profileData.services));
  if (profileData.credentials) formDataPayload.append('credentials', JSON.stringify(profileData.credentials));
  if (profileData.pricing) formDataPayload.append('pricing', JSON.stringify(profileData.pricing));
  if (profileData.availability) formDataPayload.append('availability', JSON.stringify(profileData.availability));

  if (profileData.avatar) {
    formDataPayload.append('avatar', profileData.avatar);
  }

  if (profileData.pictures && profileData.pictures.length > 0) {
    profileData.pictures.forEach((picture) => {
      formDataPayload.append('pictures', picture);
    });
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/provider/profile`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      data: data,
      message: data.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getAllProviders = async (
  params: GetAllProvidersRequest = {}
): Promise<GetAllProvidersResponse> => {
  const { page = 1, limit = 10, typeOfCare, search } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());
  if (typeOfCare) queryParams.append('typeOfCare', typeOfCare);
  if (search) queryParams.append('search', search);

  console.log('Fetching all providers with params:', params);

  const response = await apiClient<any>(
    `/api/provider/providers?${queryParams.toString()}`,
    { method: 'GET' }
  );

  console.log('Get all providers response:', response);

  if (response.success && response.data) {
    const transformedProviders = (response.data.providers || [])
      .map((provider: any) => ({
        id: provider.providerId?.toString() || provider._id,
        _id: provider._id,
        providerId: provider.providerId,
        name: provider.name,
        businessName: provider.businessName,
        email: provider.email,
        typeOfCare: Array.isArray(provider.typeOfCare) ? provider.typeOfCare.join(', ') : provider.typeOfCare,
        careTypes: provider.careTypes,
        location: provider.location || provider.city,
        cqcRating: provider.cqcRating,
        startingPrice: provider.pricing?.hourly || provider.startingPrice,
        verified: provider.isVerified ?? provider.verified,
        isVerified: provider.isVerified,
        isBlocked: provider.isBlocked,
        avatar: provider.avatar,
        services: provider.services,
        pricing: provider.pricing,
        pictures: provider.pictures,
        gallery: provider.pictures || provider.gallery,
      }))
      .filter((provider: any) => !provider.isBlocked);

    console.log('Filtered out blocked providers. Total providers shown:', transformedProviders.length);

    return {
      success: true,
      totalProviders: transformedProviders.length,
      currentPage: response.data.currentPage || page,
      totalPages: Math.ceil(transformedProviders.length / limit),
      providers: transformedProviders,
      message: response.data.message,
    };
  }

  return {
    success: false,
    totalProviders: 0,
    currentPage: page,
    totalPages: 0,
    providers: [],
    message: response.error || 'Failed to fetch providers',
  };
};

export const searchProvidersByLocation = async (
  searchParams: GeoSearchRequest
): Promise<GeoSearchResponse> => {
  console.log('Search request:', searchParams);

  const response = await apiClient<any>('/api/provider/search', {
    method: 'POST',
    body: JSON.stringify(searchParams),
  });

  console.log('Search response:', response);

  if (response.success && response.data) {
    const transformedProviders = (response.data.providers || [])
      .map((provider: any) => ({
        id: provider.providerId?.toString() || provider._id,
        _id: provider._id,
        providerId: provider.providerId,
        name: provider.name,
        businessName: provider.businessName,
        email: provider.email,
        typeOfCare: provider.typeOfCare,
        careTypes: provider.careTypes,
        location: provider.location || provider.city,
        distanceMiles: provider.distanceMiles,
        rankScore: provider.rankScore,
        cqcRating: provider.cqcRating,
        startingPrice: provider.pricing?.hourly || provider.startingPrice,
        verified: provider.isVerified ?? provider.verified,
        isVerified: provider.isVerified,
        isBlocked: provider.isBlocked,
        avatar: provider.avatar,
        services: provider.services,
        pricing: provider.pricing,
        pictures: provider.pictures,
        gallery: provider.pictures || provider.gallery,
      }))
      .filter((provider: any) => !provider.isBlocked);

    console.log('Filtered out blocked providers from search. Total found:', transformedProviders.length);

    return {
      success: true,
      searchCenter: response.data.searchCenter,
      radiusMiles: response.data.radiusMiles,
      totalFound: transformedProviders.length,
      providers: transformedProviders,
      message: response.data.message,
    };
  }

  return {
    success: false,
    radiusMiles: searchParams.radiusMiles || 20,
    totalFound: 0,
    providers: [],
    message: response.error || 'Failed to search providers',
  };
};

export const createBooking = async (
  bookingData: CreateBookingRequest
): Promise<CreateBookingResponse> => {
  console.log('Creating booking with request data:', bookingData);

  try {
    const providerCheck = await getProviderProfile(String(bookingData.providerId));
    if (!providerCheck.success || providerCheck.provider?.isBlocked) {
      return {
        success: false,
        message: 'This provider is no longer accepting bookings',
        booking: null as any,
      };
    }
  } catch (err) {
    console.error('Error checking provider status:', err);
  }

  const response = await apiClient<any>('/api/booking/create', {
    method: 'POST',
    body: JSON.stringify(bookingData),
  });

  console.log('Create booking response:', response);

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Booking created successfully',
      booking: response.data.booking,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to create booking',
    booking: null as any,
  };
};

export const acceptBooking = async (
  bookingId: number
): Promise<BookingActionResponse> => {
  const response = await apiClient<any>('/api/booking/accept', {
    method: 'PUT',
    body: JSON.stringify({ bookingId }),
  });

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Booking accepted successfully',
      booking: response.data.booking,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to accept booking',
    booking: null as any,
  };
};

export const rejectBooking = async (
  bookingId: number,
  reason: string
): Promise<BookingActionResponse> => {
  const response = await apiClient<any>('/api/booking/reject', {
    method: 'PUT',
    body: JSON.stringify({ bookingId, reason }),
  });

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Booking rejected successfully',
      booking: response.data.booking,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to reject booking',
    booking: null as any,
  };
};

export const cancelBooking = async (
  bookingId: number
): Promise<BookingActionResponse> => {
  const response = await apiClient<any>('/api/booking/cancel', {
    method: 'PUT',
    body: JSON.stringify({ bookingId }),
  });

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Booking cancelled successfully',
      booking: response.data.booking,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to cancel booking',
    booking: null as any,
  };
};

export const completeBooking = async (
  bookingId: number
): Promise<BookingActionResponse> => {
  const response = await apiClient<any>('/api/booking/complete', {
    method: 'PUT',
    body: JSON.stringify({ bookingId }),
  });

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Booking marked as completed',
      booking: response.data.booking,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to complete booking',
    booking: null as any,
  };
};

export const getBookingDetails = async (
  bookingId: number
): Promise<BookingDetailsResponse> => {
  const response = await apiClient<any>('/api/booking/details', {
    method: 'POST',
    body: JSON.stringify({ bookingId }),
  });

  if (response.success && response.data) {
    const data = response.data;
    const booking = data.booking || data;
    return {
      success: true,
      booking: {
        ...booking,
        providerDetails: data.providerDetails || booking.providerDetails,
        userDetails: data.userDetails || booking.userDetails,
      },
    };
  }

  return {
    success: false,
    booking: null as any,
  };
};

export const getMyBookings = async (): Promise<ApiResponse<{ total: number; bookings: any[] }>> => {
  const response = await apiClient<{ total: number; bookings: any[] }>('/api/booking/history/provider', {
    method: 'GET',
  });

  if (response.success && response.data) {
    return {
      success: true,
      data: {
        total: response.data.total || 0,
        bookings: response.data.bookings || [],
      },
      message: response.message,
    };
  }

  return {
    success: false,
    error: response.error || 'Failed to fetch bookings',
  };
};

export const getUserBookings = async (): Promise<ApiResponse<{ total: number; bookings: any[] }>> => {
  const response = await apiClient<{ total: number; bookings: any[] }>('/api/booking/history/user', {
    method: 'GET',
  });

  if (response.success && response.data) {
    return {
      success: true,
      data: {
        total: response.data.total || 0,
        bookings: response.data.bookings || [],
      },
      message: response.message,
    };
  }

  return {
    success: false,
    error: response.error || 'Failed to fetch user bookings',
  };
};

export const getUpcomingBookings = async (): Promise<UpcomingBookingsResponse> => {
  const response = await apiClient<{ count: number; bookings: any[] }>('/api/booking/upcoming', {
    method: 'POST',
  });

  if (response.success && response.data) {
    return {
      success: true,
      count: response.data.count || 0,
      bookings: response.data.bookings || [],
    };
  }

  return {
    success: false,
    count: 0,
    bookings: [],
    error: response.error || 'Failed to fetch upcoming bookings',
  };
};

export const getBookingHistory = async (
  params: GetBookingHistoryRequest = {}
): Promise<BookingHistoryResponse> => {
  const { page = 1, limit = 10 } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());

  const response = await apiClient<any>(`/api/booking/history?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (response.success && response.data) {
    const bookings = response.data.data || response.data.bookings || [];
    const pagination = response.data.pagination || {
      totalCount: response.data.total || bookings.length,
      currentPage: page,
      totalPages: Math.ceil((response.data.total || bookings.length) / limit),
    };

    return {
      success: true,
      data: bookings,
      pagination: {
        totalCount: pagination.totalCount ?? pagination.total ?? bookings.length,
        currentPage: pagination.currentPage ?? pagination.page ?? page,
        totalPages: pagination.totalPages ?? 1,
      },
      message: response.data.message,
    };
  }

  return {
    success: false,
    data: [],
    pagination: { totalCount: 0, currentPage: page, totalPages: 0 },
    error: response.error || 'Failed to fetch booking history',
  };
};

export const createUserProfile = async (
  formData: UserProfileFormData
): Promise<UserProfileResponse> => {
  const formDataPayload = new FormData();

  formDataPayload.append('userId', formData.userId);
  formDataPayload.append('name', formData.name);
  formDataPayload.append('phone', formData.phone);
  formDataPayload.append('address', formData.address);
  formDataPayload.append('city', formData.city);
  formDataPayload.append('postalCode', formData.postalCode);
  formDataPayload.append('houseType', formData.houseType);
  formDataPayload.append('bookingFor', formData.bookingFor);
  formDataPayload.append('recipientName', formData.recipientName);
  formDataPayload.append('recipientAge', formData.recipientAge.toString());
  formDataPayload.append('recipientGender', formData.recipientGender);
  formDataPayload.append('mobilityStatus', formData.mobilityStatus);
  formDataPayload.append('careShift', formData.careShift);
  formDataPayload.append('careFrequency', formData.careFrequency);
  formDataPayload.append('emergencyContactName', formData.emergencyContactName);
  formDataPayload.append('emergencyContactPhone', formData.emergencyContactPhone);
  formDataPayload.append('relationship', formData.relationship);

  if (formData.bio) {
    formDataPayload.append('bio', formData.bio);
  }

  if (formData.floorNumber) {
    formDataPayload.append('floorNumber', formData.floorNumber);
  }

  if (formData.hasLift !== undefined) {
    formDataPayload.append('hasLift', String(formData.hasLift));
  }

  if (formData.petsAtHome !== undefined) {
    formDataPayload.append('petsAtHome', String(formData.petsAtHome));
  }

  if (formData.height) {
    formDataPayload.append('height', formData.height);
  }

  if (formData.weight) {
    formDataPayload.append('weight', formData.weight);
  }

  if (formData.allergies) {
    formDataPayload.append('allergies', formData.allergies);
  }

  if (formData.currentMedications) {
    formDataPayload.append('currentMedications', formData.currentMedications);
  }

  if (formData.requiresMedicalEquipment !== undefined) {
    formDataPayload.append('requiresMedicalEquipment', String(formData.requiresMedicalEquipment));
  }

  if (formData.equipmentDetails) {
    formDataPayload.append('equipmentDetails', formData.equipmentDetails);
  }

  if (formData.mentalCondition) {
    formDataPayload.append('mentalCondition', formData.mentalCondition);
  }

  if (formData.specialInstructions) {
    formDataPayload.append('specialInstructions', formData.specialInstructions);
  }

  if (formData.preferredCaregiverGender) {
    formDataPayload.append('preferredCaregiverGender', formData.preferredCaregiverGender);
  }

  formDataPayload.append('medicalConditions', JSON.stringify(formData.medicalConditions));
  formDataPayload.append('preferredCareTypes', JSON.stringify(formData.preferredCareTypes));
  formDataPayload.append('languagesPreferred', JSON.stringify(formData.languagesPreferred));

  if (formData.photo) {
    formDataPayload.append('photo', formData.photo);
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      profile: data.profile,
      message: data.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getUserProfile = async (): Promise<UserProfileResponse> => {
  const response = await apiClient<any>('/api/user/profile', {
    method: 'GET',
  });

  if (response.success && response.data) {
    return {
      success: true,
      profile: response.data.profile,
      message: response.data.message,
    };
  }

  return {
    success: false,
    error: response.error || 'Failed to fetch user profile',
  };
};

export const updateUserProfile = async (
  profileData: UpdateUserProfileData
): Promise<UserProfileResponse> => {
  const formDataPayload = new FormData();

  if (profileData.name) formDataPayload.append('name', profileData.name);
  if (profileData.phone) formDataPayload.append('phone', profileData.phone);
  if (profileData.bio) formDataPayload.append('bio', profileData.bio);
  if (profileData.address) formDataPayload.append('address', profileData.address);
  if (profileData.city) formDataPayload.append('city', profileData.city);
  if (profileData.postalCode) formDataPayload.append('postalCode', profileData.postalCode);
  if (profileData.houseType) formDataPayload.append('houseType', profileData.houseType);
  if (profileData.floorNumber) formDataPayload.append('floorNumber', profileData.floorNumber);
  if (profileData.hasLift !== undefined) formDataPayload.append('hasLift', String(profileData.hasLift));
  if (profileData.petsAtHome !== undefined) formDataPayload.append('petsAtHome', String(profileData.petsAtHome));
  if (profileData.bookingFor) formDataPayload.append('bookingFor', profileData.bookingFor);
  if (profileData.recipientName) formDataPayload.append('recipientName', profileData.recipientName);
  if (profileData.recipientAge !== undefined) formDataPayload.append('recipientAge', profileData.recipientAge.toString());
  if (profileData.recipientGender) formDataPayload.append('recipientGender', profileData.recipientGender);
  if (profileData.height) formDataPayload.append('height', profileData.height);
  if (profileData.weight) formDataPayload.append('weight', profileData.weight);
  if (profileData.mobilityStatus) formDataPayload.append('mobilityStatus', profileData.mobilityStatus);
  if (profileData.allergies) formDataPayload.append('allergies', profileData.allergies);
  if (profileData.currentMedications) formDataPayload.append('currentMedications', profileData.currentMedications);
  if (profileData.requiresMedicalEquipment !== undefined) formDataPayload.append('requiresMedicalEquipment', String(profileData.requiresMedicalEquipment));
  if (profileData.equipmentDetails) formDataPayload.append('equipmentDetails', profileData.equipmentDetails);
  if (profileData.mentalCondition) formDataPayload.append('mentalCondition', profileData.mentalCondition);
  if (profileData.specialInstructions) formDataPayload.append('specialInstructions', profileData.specialInstructions);
  if (profileData.careShift) formDataPayload.append('careShift', profileData.careShift);
  if (profileData.careFrequency) formDataPayload.append('careFrequency', profileData.careFrequency);
  if (profileData.preferredCaregiverGender) formDataPayload.append('preferredCaregiverGender', profileData.preferredCaregiverGender);
  if (profileData.emergencyContactName) formDataPayload.append('emergencyContactName', profileData.emergencyContactName);
  if (profileData.emergencyContactPhone) formDataPayload.append('emergencyContactPhone', profileData.emergencyContactPhone);
  if (profileData.relationship) formDataPayload.append('relationship', profileData.relationship);

  if (profileData.medicalConditions !== undefined) {
    console.log('Sending medicalConditions:', profileData.medicalConditions);
    formDataPayload.append('medicalConditions', JSON.stringify(profileData.medicalConditions));
  }
  if (profileData.preferredCareTypes !== undefined) {
    console.log('Sending preferredCareTypes:', profileData.preferredCareTypes);
    formDataPayload.append('preferredCareTypes', JSON.stringify(profileData.preferredCareTypes));
  }
  if (profileData.languagesPreferred !== undefined) {
    console.log('Sending languagesPreferred:', profileData.languagesPreferred);
    formDataPayload.append('languagesPreferred', JSON.stringify(profileData.languagesPreferred));
  }

  if (profileData.photo) {
    formDataPayload.append('photo', profileData.photo);
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      profile: data.profile,
      message: data.message,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const submitReview = async (
  reviewData: CreateReviewRequest
): Promise<CreateReviewResponse> => {
  console.log('Submitting review:', reviewData);

  const response = await apiClient<any>('/api/review/add', {
    method: 'POST',
    body: JSON.stringify(reviewData),
  });

  console.log('Submit review response:', response);

  if (response.success && response.data) {
    return {
      success: true,
      message: response.data.message || 'Review submitted successfully',
      review: response.data.review,
    };
  }

  return {
    success: false,
    message: response.error || 'Failed to submit review',
    review: null as any,
  };
};

export const getProviderReviews = async (): Promise<GetProviderReviewsResponse> => {
  const response = await apiClient<any>('/api/review/provider', {
    method: 'POST',
  });

  if (response.success && response.data) {
    const reviews = response.data.reviews || [];
    const averageRating = reviews.length > 0
      ? reviews.reduce((sum: number, review: Review) => sum + review.rating, 0) / reviews.length
      : 0;

    return {
      success: true,
      summary: {
        averageRating,
        totalReviews: response.data.count || reviews.length,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      },
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 1,
      },
      reviews,
      message: response.data.message,
    };
  }

  return {
    success: false,
    summary: {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    },
    pagination: {
      page: 1,
      limit: 10,
      totalPages: 0,
    },
    reviews: [],
    error: response.error || 'Failed to fetch reviews',
  };
};

export const getProviderReviewsByProviderId = async (
  providerId: number,
  page: number = 1,
  limit: number = 10
): Promise<GetProviderReviewsResponse> => {
  const response = await apiClient<any>('/api/review/provider-reviews', {
    method: 'POST',
    body: JSON.stringify({ providerId, page, limit }),
  });

  if (response.success && response.data) {
    const allReviews = response.data.reviews || [];
    const filteredReviews = allReviews.filter(
      (review: Review) => !review.isFlagged && !review.isHidden
    );

    return {
      success: true,
      summary: response.data.summary || {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      },
      pagination: response.data.pagination || {
        page,
        limit,
        totalPages: 1,
      },
      reviews: filteredReviews,
      message: response.data.message,
    };
  }

  return {
    success: false,
    summary: {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    },
    pagination: {
      page,
      limit,
      totalPages: 0,
    },
    reviews: [],
    error: response.error || 'Failed to fetch provider reviews',
  };
};

export const createPost = async (
  formData: CreatePostFormData
): Promise<CreatePostResponse> => {
  const formDataPayload = new FormData();

  formDataPayload.append('title', formData.title);
  formDataPayload.append('description', formData.description);
  formDataPayload.append('careType', formData.careType);
  formDataPayload.append('city', formData.city);
  formDataPayload.append('postalCode', formData.postalCode);
  formDataPayload.append('appointmentDate', formData.appointmentDate);
  formDataPayload.append('budget', formData.budget.toString());

  if (formData.photo) {
    formDataPayload.append('photo', formData.photo);
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/post/create`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      message: data.message || 'Post created successfully',
      post: data.post,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        post: null as any,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      post: null as any,
    };
  }
};

export const updatePost = async (
  formData: UpdatePostFormData
): Promise<UpdatePostResponse> => {
  const formDataPayload = new FormData();

  formDataPayload.append('postId', formData.postId.toString());

  if (formData.title !== undefined) formDataPayload.append('title', formData.title);
  if (formData.description !== undefined) formDataPayload.append('description', formData.description);
  if (formData.careType !== undefined) formDataPayload.append('careType', formData.careType);
  if (formData.city !== undefined) formDataPayload.append('city', formData.city);
  if (formData.postalCode !== undefined) formDataPayload.append('postalCode', formData.postalCode);
  if (formData.budget !== undefined) formDataPayload.append('budget', formData.budget.toString());
  if (formData.appointmentDate !== undefined) formDataPayload.append('appointmentDate', formData.appointmentDate);
  if (formData.status !== undefined) formDataPayload.append('status', formData.status);

  if (formData.photo) {
    formDataPayload.append('photo', formData.photo);
  }

  try {
    const token = localStorage.getItem('authToken');

    const response = await fetch(`${API_BASE_URL}/api/post/update`, {
      method: 'PUT',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formDataPayload,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Request failed. Please try again.';
      throw new ApiError(response.status, errorMessage);
    }

    return {
      success: true,
      message: data.message || 'Post updated successfully',
      post: data.post,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        post: null as any,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      post: null as any,
    };
  }
};

export const getUserPosts = async (): Promise<GetUserPostsResponse> => {
  const response = await apiClient<{ posts: any[] }>('/api/post/my-posts', {
    method: 'GET',
  });

  if (response.success && response.data) {
    return {
      success: true,
      posts: response.data.posts || response.data,
      totalPosts: response.data.posts?.length || 0,
      message: response.message,
    };
  }

  return {
    success: false,
    posts: [],
    error: response.error || 'Failed to fetch posts',
  };
};

export const deletePost = async (postId: number): Promise<DeletePostResponse> => {
  try {
    const data = await apiClient<DeletePostResponse>('/api/post/delete', {
      method: 'DELETE',
      body: JSON.stringify({ postId }),
    });

    return {
      success: true,
      message: data.message || 'Post deleted successfully',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

export const getAllActivePosts = async (): Promise<GetAllPostsResponse> => {
  const response = await apiClient<{ count: number; posts: any[] }>('/api/post/all', {
    method: 'GET',
  });

  if (response.success && response.data) {
    return {
      success: true,
      count: response.data.count || response.data.posts?.length || 0,
      posts: response.data.posts || [],
      message: response.message,
    };
  }

  return {
    success: false,
    count: 0,
    posts: [],
    error: response.error || 'Failed to fetch posts',
  };
};

export const contactPostOwner = async (request: ContactPostRequest): Promise<ContactPostResponse> => {
  try {
    const data = await apiClient<ContactPostResponse>('/api/posts/contact', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return {
      success: true,
      message: data.message || 'Contact request sent successfully',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

export const startProviderChat = async (request: StartProviderChatRequest): Promise<StartProviderChatResponse> => {
  try {
    const response = await apiClient<StartProviderChatResponse>('/api/chat/provider/start', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Chat started successfully',
        chat: response.data.chat,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to start chat',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const sendUserReply = async (request: SendUserReplyRequest): Promise<SendUserReplyResponse> => {
  try {
    const response = await apiClient<SendUserReplyResponse>('/api/chat/user/reply', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Reply sent successfully',
        chat: response.data.chat,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to send reply',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const getUserChats = async (): Promise<GetUserChatsResponse> => {
  try {
    const response = await apiClient<GetUserChatsResponse>('/api/chat/user', {
      method: 'GET',
    });

    if (response.success && response.data) {
      return {
        success: true,
        chats: response.data.chats || [],
        message: response.data.message,
      };
    }

    return {
      success: false,
      chats: [],
      error: response.error || 'Failed to fetch chats',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        chats: [],
        error: error.message,
      };
    }

    return {
      success: false,
      chats: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getProviderChats = async (): Promise<GetProviderChatsResponse> => {
  try {
    const response = await apiClient<GetProviderChatsResponse>('/api/chat/provider', {
      method: 'GET',
    });

    if (response.success && response.data) {
      return {
        success: true,
        chats: response.data.chats || [],
        message: response.data.message,
      };
    }

    return {
      success: false,
      chats: [],
      error: response.error || 'Failed to fetch chats',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        chats: [],
        error: error.message,
      };
    }

    return {
      success: false,
      chats: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const sendBookNowAction = async (request: BookNowRequest): Promise<BookNowResponse> => {
  try {
    const response = await apiClient<BookNowResponse>('/api/chat/provider/booknow', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Book Now action sent successfully',
        chat: response.data.chat,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to send Book Now action',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const sendAdminOtp = async (request: AdminSendOtpRequest): Promise<AdminSendOtpResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Failed to send OTP',
      };
    }

    return {
      success: true,
      message: data.message || 'OTP sent to email successfully',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

export const verifyAdminOtp = async (request: AdminVerifyOtpRequest): Promise<AdminVerifyOtpResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Failed to verify OTP',
      };
    }

    if (data.success && data.accessToken) {
      localStorage.setItem('authToken', data.accessToken);
      const adminUser: User = {
        id: String(data.adminId),
        userId: String(data.adminId),
        email: request.email,
        name: 'Admin',
        role: 'admin',
      };
      localStorage.setItem('userData', JSON.stringify(adminUser));
    }

    return {
      success: true,
      message: data.message || 'Login successful',
      adminId: data.adminId,
      accessToken: data.accessToken,
    };
  } catch (error) {
    return {
      success: false,
      message: 'Network error. Please check your connection.',
    };
  }
};

export const getAdminProvidersList = async (request: AdminProviderListRequest): Promise<AdminProviderListResponse> => {
  try {
    const response = await apiClient<{ providers: any[] }>('/api/admin/providers/list', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        providers: response.data.providers || [],
        message: response.message,
      };
    }

    return {
      success: false,
      providers: [],
      error: response.error || 'Failed to fetch providers list',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        providers: [],
        error: error.message,
      };
    }

    return {
      success: false,
      providers: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getAdminProviderDetails = async (request: AdminProviderDetailsRequest): Promise<AdminProviderDetailsResponse> => {
  try {
    const response = await apiClient<{ provider: any }>('/api/admin/providers/details', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        provider: response.data.provider,
        message: response.message,
      };
    }

    return {
      success: false,
      provider: null as any,
      error: response.error || 'Failed to fetch provider details',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        provider: null as any,
        error: error.message,
      };
    }

    return {
      success: false,
      provider: null as any,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const blockProvider = async (request: AdminBlockProviderRequest): Promise<AdminBlockProviderResponse> => {
  try {
    const response = await apiClient<any>('/api/admin/providers/block', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Provider action completed successfully',
        provider: response.data.provider,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to update provider status',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const updateAdminNote = async (request: AdminUpdateNoteRequest): Promise<AdminUpdateNoteResponse> => {
  try {
    const response = await apiClient<any>('/api/admin/providers/note', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success) {
      return {
        success: true,
        message: response.message || 'Note updated successfully',
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to update note',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const getNotifications = async (): Promise<GetNotificationsResponse> => {
  try {
    const response = await apiClient<GetNotificationsResponse>('/api/web-notification', {
      method: 'GET',
    });

    if (response.success && response.data) {
      return {
        success: true,
        role: response.data.role,
        recipientId: response.data.recipientId,
        notifications: response.data.notifications || [],
        unreadCount: response.data.unreadCount || 0,
        message: response.data.message,
      };
    }

    return {
      success: false,
      role: '',
      recipientId: 0,
      notifications: [],
      unreadCount: 0,
      error: response.error || 'Failed to fetch notifications',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        role: '',
        recipientId: 0,
        notifications: [],
        unreadCount: 0,
        error: error.message,
      };
    }

    return {
      success: false,
      role: '',
      recipientId: 0,
      notifications: [],
      unreadCount: 0,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const markNotificationAsRead = async (notificationId: string): Promise<MarkNotificationAsReadResponse> => {
  try {
    const response = await apiClient<MarkNotificationAsReadResponse>(`/api/web-notification/read/${notificationId}`, {
      method: 'PUT',
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Notification marked as read',
        notification: response.data.notification,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to mark notification as read',
      notification: null as any,
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        notification: null as any,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      notification: null as any,
      error: 'Network error',
    };
  }
};

export const markAllNotificationsAsRead = async (): Promise<MarkAllNotificationsAsReadResponse> => {
  try {
    const response = await apiClient<MarkAllNotificationsAsReadResponse>('/api/web-notification/read-all', {
      method: 'PUT',
    });

    if (response.success) {
      return {
        success: true,
        message: response.message || 'All notifications marked as read',
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to mark all notifications as read',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const deleteNotification = async (notificationId: string): Promise<DeleteNotificationResponse> => {
  try {
    const response = await apiClient<DeleteNotificationResponse>(`/api/web-notification/${notificationId}`, {
      method: 'DELETE',
    });

    if (response.success) {
      return {
        success: true,
        message: response.message || 'Notification deleted successfully',
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to delete notification',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const getAdminDashboardSummary = async (): Promise<DashboardSummaryResponse> => {
  try {
    const response = await apiClient<{ success: boolean; data: DashboardSummary }>('/api/admin/dashboard-summary', {
      method: 'GET',
    });

    if (response.success && response.data?.data) {
      return { success: true, data: response.data.data };
    }

    return { success: false, error: response.error || 'Failed to fetch dashboard summary' };
  } catch (error) {
    if (error instanceof ApiError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

export const getLowRatingReviews = async (): Promise<LowRatingReviewsResponse> => {
  try {
    const response = await apiClient<{ total: number; reviews: Review[] }>('/api/admin/reviews/low-rating', {
      method: 'POST',
    });

    if (response.success && response.data) {
      return {
        success: true,
        total: response.data.total || response.data.reviews.length,
        reviews: response.data.reviews || [],
        message: response.message,
      };
    }

    return {
      success: false,
      total: 0,
      reviews: [],
      error: response.error || 'Failed to fetch low-rating reviews',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        total: 0,
        reviews: [],
        error: error.message,
      };
    }

    return {
      success: false,
      total: 0,
      reviews: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getAdminReviewsList = async (request: AdminReviewListRequest): Promise<AdminReviewListResponse> => {
  try {
    const response = await apiClient<{ total: number; page?: number; reviews: Review[] }>('/api/admin/reviews/list', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      const raw = (response.data as any).data || response.data;
      return {
        success: true,
        total: raw.total ?? response.data.total ?? raw.reviews?.length ?? 0,
        page: raw.page ?? response.data.page,
        reviews: raw.reviews ?? response.data.reviews ?? [],
        message: response.message,
      };
    }

    return {
      success: false,
      total: 0,
      reviews: [],
      error: response.error || 'Failed to fetch reviews',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        total: 0,
        reviews: [],
        error: error.message,
      };
    }

    return {
      success: false,
      total: 0,
      reviews: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const approveReview = async (reviewId: number): Promise<AdminReviewActionResponse> => {
  try {
    const response = await apiClient<{ message: string; review: Review }>('/api/admin/reviews/approve', {
      method: 'POST',
      body: JSON.stringify({ reviewId }),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Review approved successfully',
        review: response.data.review,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to approve review',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const rejectReview = async (reviewId: number, reason: string): Promise<AdminReviewActionResponse> => {
  try {
    const response = await apiClient<{ message: string; review: Review }>('/api/admin/reviews/reject', {
      method: 'POST',
      body: JSON.stringify({ reviewId, reason }),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Review rejected successfully',
        review: response.data.review,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to reject review',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const deleteReview = async (reviewId: number): Promise<AdminReviewActionResponse> => {
  try {
    const response = await apiClient<{ message: string }>('/api/admin/reviews/delete', {
      method: 'POST',
      body: JSON.stringify({ reviewId }),
    });

    if (response.success) {
      return {
        success: true,
        message: response.data?.message || 'Review deleted successfully',
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to delete review',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const getAdminBookingsList = async (request: AdminBookingListRequest): Promise<AdminBookingListResponse> => {
  try {
    const response = await apiClient<{ bookings: any[]; total?: number; page?: number }>('/api/admin/bookings/list', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      const raw = (response.data as any).data || response.data;
      const bookings: any[] = raw.bookings || raw || [];
      return {
        success: true,
        bookings: bookings.map((b: any) => ({
          ...b,
          status: b.status?.toLowerCase(),
        })),
        total: raw.total ?? (response.data as any).total,
        page: raw.page ?? (response.data as any).page,
        message: response.message,
      };
    }

    return {
      success: false,
      bookings: [],
      error: response.error || 'Failed to fetch bookings',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        bookings: [],
        error: error.message,
      };
    }

    return {
      success: false,
      bookings: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getAdminBookingDetails = async (request: AdminBookingDetailsRequest): Promise<AdminBookingDetailsResponse> => {
  try {
    const response = await apiClient<any>('/api/admin/bookings/details', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      const raw = (response.data as any).data || response.data;
      return {
        success: true,
        booking: { ...raw, status: raw.status?.toLowerCase() },
        message: response.message,
      };
    }

    return {
      success: false,
      booking: null as any,
      error: response.error || 'Failed to fetch booking details',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        booking: null as any,
        error: error.message,
      };
    }

    return {
      success: false,
      booking: null as any,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const updateAdminBookingStatus = async (request: AdminUpdateBookingStatusRequest): Promise<AdminUpdateBookingStatusResponse> => {
  try {
    const response = await apiClient<{ message: string; booking: any }>('/api/admin/bookings/update-status', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Booking status updated successfully',
        booking: response.data.booking,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to update booking status',
      booking: null as any,
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        booking: null as any,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      booking: null as any,
      error: 'Network error',
    };
  }
};

export const cancelAdminBooking = async (request: AdminCancelBookingRequest): Promise<AdminCancelBookingResponse> => {
  try {
    const response = await apiClient<{ message: string; booking: any }>('/api/admin/bookings/cancel', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Booking cancelled successfully',
        booking: response.data.booking,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to cancel booking',
      booking: null as any,
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        booking: null as any,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      booking: null as any,
      error: 'Network error',
    };
  }
};

export const getAdminDeleteRequests = async (request: GetDeleteRequestsRequest): Promise<GetDeleteRequestsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (request.status) queryParams.append('status', request.status);

    const response = await apiClient<any>(`/api/admin/delete-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, {
      method: 'GET',
    });

    if (response.success && response.data) {
      const transformed = transformDeleteRequestsResponse(response.data);

      return {
        success: true,
        requests: transformed.requests,
        message: response.message,
      };
    }

    return {
      success: false,
      requests: [],
      error: response.error || 'Failed to fetch delete requests',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        requests: [],
        error: error.message,
      };
    }

    return {
      success: false,
      requests: [],
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getDeleteRequestDetails = async (requestId: string): Promise<{ success: boolean; request?: any; error?: string }> => {
  try {
    const response = await apiClient<any>(`/api/admin/delete-requests?requestId=${requestId}`, {
      method: 'GET',
    });

    if (response.success && response.data) {
      const transformed = transformDeleteRequestsResponse(response.data);

      if (transformed.requests && transformed.requests.length > 0) {
        return {
          success: true,
          request: transformed.requests.find(r => r.requestId === parseInt(requestId)),
        };
      }

      return {
        success: false,
        error: 'Delete request not found',
      };
    }

    return {
      success: false,
      error: response.error || 'Failed to fetch delete request details',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const searchDeleteRequests = async (request: SearchDeleteRequestsRequest): Promise<SearchDeleteRequestsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (request.userId) queryParams.append('userId', request.userId.toString());
    if (request.providerId) queryParams.append('providerId', request.providerId.toString());
    if (request.name) queryParams.append('name', request.name);
    if (request.status) queryParams.append('status', request.status);
    if (request.type) queryParams.append('type', request.type);
    if (request.page) queryParams.append('page', request.page.toString());
    if (request.limit) queryParams.append('limit', request.limit.toString());

    const response = await apiClient<any>(`/api/admin/delete-requests/search?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (response.success && response.data) {
      const transformed = transformDeleteRequestsResponse(response.data);

      return {
        success: true,
        requests: transformed.requests,
        total: transformed.total,
        page: transformed.page,
        totalPages: transformed.totalPages,
        message: response.message,
      };
    }

    return {
      success: false,
      requests: [],
      total: 0,
      page: 1,
      totalPages: 1,
      error: response.error || 'Failed to search delete requests',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        requests: [],
        total: 0,
        page: 1,
        totalPages: 1,
        error: error.message,
      };
    }

    return {
      success: false,
      requests: [],
      total: 0,
      page: 1,
      totalPages: 1,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const getDeleteRequestsDashboard = async (request: DeleteRequestDashboardRequest): Promise<DeleteRequestDashboardResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (request.startDate) queryParams.append('startDate', request.startDate);
    if (request.endDate) queryParams.append('endDate', request.endDate);
    if (request.status) queryParams.append('status', request.status);
    if (request.type) queryParams.append('type', request.type);
    if (request.page) queryParams.append('page', request.page.toString());
    if (request.limit) queryParams.append('limit', request.limit.toString());

    const response = await apiClient<any>(`/api/admin/delete-requests/dashboard?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (response.success && response.data) {
      const transformed = transformDeleteRequestsResponse(response.data);

      return {
        success: true,
        stats: transformed.stats || {
          totalRequests: 0,
          pendingRequests: 0,
          approvedThisMonth: 0,
          rejectedThisMonth: 0,
          userRequests: 0,
          providerRequests: 0,
        },
        requests: transformed.requests,
        total: transformed.total,
        page: transformed.page,
        totalPages: transformed.totalPages,
        message: response.message,
      };
    }

    return {
      success: false,
      stats: {
        totalRequests: 0,
        pendingRequests: 0,
        approvedThisMonth: 0,
        rejectedThisMonth: 0,
        userRequests: 0,
        providerRequests: 0,
      },
      requests: [],
      total: 0,
      page: 1,
      totalPages: 1,
      error: response.error || 'Failed to fetch delete requests dashboard',
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        stats: {
          totalRequests: 0,
          pendingRequests: 0,
          approvedThisMonth: 0,
          rejectedThisMonth: 0,
          userRequests: 0,
          providerRequests: 0,
        },
        requests: [],
        total: 0,
        page: 1,
        totalPages: 1,
        error: error.message,
      };
    }

    return {
      success: false,
      stats: {
        totalRequests: 0,
        pendingRequests: 0,
        approvedThisMonth: 0,
        rejectedThisMonth: 0,
        userRequests: 0,
        providerRequests: 0,
      },
      requests: [],
      total: 0,
      page: 1,
      totalPages: 1,
      error: 'Network error. Please check your connection.',
    };
  }
};

export const approveDeleteRequest = async (request: ApproveDeleteRequestRequest): Promise<ApproveDeleteRequestResponse> => {
  try {
    const response = await apiClient<{ message: string; request: any }>('/api/admin/delete/approve', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Delete request approved successfully',
        request: response.data.request,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to approve delete request',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const rejectDeleteRequest = async (request: RejectDeleteRequestRequest): Promise<RejectDeleteRequestResponse> => {
  try {
    const response = await apiClient<{ message: string; request: any }>('/api/admin/delete/reject', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Delete request rejected successfully',
        request: response.data.request,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to reject delete request',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const bulkApproveDeleteRequests = async (request: BulkApproveDeleteRequestsRequest): Promise<BulkApproveDeleteRequestsResponse> => {
  try {
    const response = await apiClient<any>('/api/admin/delete/bulk-approve', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Delete requests approved successfully',
        processedRequests: response.data.processedRequests || response.data.results,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to approve delete requests',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const bulkRejectDeleteRequests = async (request: BulkRejectDeleteRequestsRequest): Promise<BulkRejectDeleteRequestsResponse> => {
  try {
    const response = await apiClient<any>('/api/admin/delete/bulk-reject', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Delete requests rejected successfully',
        processedRequests: response.data.processedRequests || response.data.results,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to reject delete requests',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};

export const reactivateAccount = async (request: ReactivateAccountRequest): Promise<ReactivateAccountResponse> => {
  try {
    const response = await apiClient<{ message: string; data: { id: string; email: string; type: 'user' | 'provider' } }>('/api/admin/reactivate-account', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || 'Account reactivated successfully',
        data: response.data.data,
      };
    }

    return {
      success: false,
      message: response.error || 'Failed to reactivate account',
      error: response.error,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        success: false,
        message: error.message,
        error: error.message,
      };
    }

    return {
      success: false,
      message: 'Network error. Please check your connection.',
      error: 'Network error',
    };
  }
};
